import {
    ClockIcon,
    CheckCircleIcon,
    ArchiveBoxArrowDownIcon,
} from '@heroicons/react/24/outline';
import { Spinner } from '@/components/ui/Spinner';

interface LedgerStatusIconProps {
    isArchived: boolean;
    needsUpdate: boolean;
    onConfirmUpdate?: () => void;
    onRevertUpdate?: () => void;
    isLoading?: boolean;
}

/**
 * Displays the update status of a ledger:
 * - Spinner: loading state during mutation
 * - Clock icon (yellow): needs update - latest value date is before today's confirmed date
 * - Checkmark (accent): up-to-date - ledger has been confirmed as current
 * - Archive icon (gray): ledger is archived
 */
export function LedgerStatusIcon({
    isArchived,
    needsUpdate,
    onConfirmUpdate,
    onRevertUpdate,
    isLoading,
}: LedgerStatusIconProps) {
    if (isArchived) {
        return (
            <span
                className="text-gray-400 dark:text-gray-500"
                title="Archived"
            >
                <ArchiveBoxArrowDownIcon className="h-6 w-6" />
            </span>
        );
    }

    if (needsUpdate) {
        return (
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onConfirmUpdate?.();
                }}
                disabled={isLoading}
                className="text-yellow-500 hover:text-yellow-600 transition-colors disabled:opacity-50"
                title="Mark as up-to-date"
            >
                {isLoading ? (
                    <Spinner size="sm" className="h-6 w-6" inheritColor />
                ) : (
                    <ClockIcon className="h-6 w-6" />
                )}
            </button>
        );
    }

    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRevertUpdate?.();
            }}
            disabled={isLoading}
            className="text-accent opacity-80 hover:opacity-100 transition-opacity disabled:opacity-50"
            title="Reset to latest value date"
        >
            {isLoading ? (
                <Spinner size="sm" className="h-6 w-6" inheritColor />
            ) : (
                <CheckCircleIcon className="h-6 w-6" />
            )}
        </button>
    );
}

/**
 * Determines if a ledger needs an update based on its confirmedUpdatedDate.
 * A ledger needs update if confirmedUpdatedDate is null or before the user's
 * latest recorded value date.
 *
 * @param confirmedUpdatedDate - Ledger's confirmed date as Unix timestamp
 * @param userMaxValueDate - User's max recorded value date as Unix timestamp.
 *        If null/undefined, falls back to comparing against today's date.
 */
export function ledgerNeedsUpdate(
    confirmedUpdatedDate: number | null | undefined,
    userMaxValueDate?: number | null
): boolean {
    if (!confirmedUpdatedDate) return true;

    // If userMaxValueDate is provided, compare against it
    if (userMaxValueDate != null) {
        return confirmedUpdatedDate < userMaxValueDate;
    }

    // Fallback: compare against today (for contexts without batch data)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const confirmedDate = new Date(confirmedUpdatedDate * 1000);
    confirmedDate.setHours(0, 0, 0, 0);
    return confirmedDate < today;
}
